/**
 * Per-instance colour + alpha + atlas-rect support for FX instanced meshes.
 *
 * WHY THIS EXISTS
 * ---------------
 * ARCHITECTURE §4.4 forbids constructing materials — `look` owns them, and fx asks for
 * `fx.particle.soft` / `fx.particle.hard` / `fx.decal` / `fx.spray` / `fx.trail` /
 * `fx.sparks`. But three.js `InstancedMesh` has no per-instance *alpha* and no per-instance
 * UV rect, and both are non-negotiable for a particle system: without them every effect
 * needs its own mesh, its own material and its own draw call.
 *
 * So fx takes a PRIVATE clone via `ctx.materials.instanceOf(key)` — which §4.4 explicitly
 * says may be mutated — and injects three instanced attributes into it:
 *
 *     aFxColor  vec3   per-instance tint (linear)
 *     aFxAlpha  float  per-instance opacity
 *     aFxRect   vec4   per-instance atlas sub-rect (u0, v0, du, dv)
 *
 * The fragment injection lands immediately before `<tonemapping_fragment>`, so `look`'s
 * tonemapping, colour space and fog still apply to particles exactly as they apply to the
 * rest of the frame. fx never touches the shared material.
 *
 * ASK FOR `look`: if the fx.* materials are ever authored as raw ShaderMaterials, keep the
 * three attribute names above declared, or expose them from the library. The string-patch
 * path below is a best-effort fallback, not a contract.
 */

import * as THREE from 'three';

const VERT_DECL = /* glsl */`
attribute vec3 aFxColor;
attribute float aFxAlpha;
attribute vec4 aFxRect;
varying vec3 vFxColor;
varying float vFxAlpha;
varying vec2 vFxUv;
`;

// Quads are PlaneGeometry(1,1) centred on the origin, so uv == position.xy + 0.5. Deriving
// it from `position` rather than the `uv` attribute keeps this independent of whichever
// USE_UV defines the base material happens to compile with.
const VERT_BODY = /* glsl */`
  vFxColor = aFxColor;
  vFxAlpha = aFxAlpha;
  vFxUv = aFxRect.xy + (position.xy + 0.5) * aFxRect.zw;
`;

const FRAG_DECL = /* glsl */`
uniform sampler2D fxMap;
uniform float fxCutoff;
uniform float fxGain;
varying vec3 vFxColor;
varying float vFxAlpha;
varying vec2 vFxUv;
`;

const FRAG_BODY = /* glsl */`
  {
    vec4 fxTex = texture2D( fxMap, vFxUv );
    float fxA = fxTex.a * vFxAlpha;
    if ( fxA < fxCutoff ) discard;
    gl_FragColor = vec4( fxTex.rgb * vFxColor * fxGain, fxA );
  }
`;

/** Injection anchors, most-preferred first. */
const FRAG_ANCHORS = ['#include <tonemapping_fragment>', '#include <colorspace_fragment>', '#include <dithering_fragment>'];
const VERT_ANCHORS = ['#include <project_vertex>', '#include <begin_vertex>'];

/**
 * @param {THREE.Material} material   a PRIVATE clone from ctx.materials.instanceOf()
 * @param {THREE.Texture} atlas
 * @param {{cutoff?:number, gain?:number, tag:string}} opts
 */
export function patchFxMaterial(material, atlas, opts) {
  const cutoff = { value: opts.cutoff != null ? opts.cutoff : 0.004 };
  const gain = { value: opts.gain != null ? opts.gain : 1.0 };
  const map = { value: atlas };

  material.userData.fx = { cutoff, gain, map };

  if (material.isShaderMaterial || material.isRawShaderMaterial) {
    // onBeforeCompile is ignored for ShaderMaterial — patch the clone's source directly.
    material.uniforms = material.uniforms || {};
    material.uniforms.fxMap = map;
    material.uniforms.fxCutoff = cutoff;
    material.uniforms.fxGain = gain;
    material.vertexShader = injectVert(material.vertexShader);
    material.fragmentShader = injectFrag(material.fragmentShader);
  } else {
    material.onBeforeCompile = (shader) => {
      shader.uniforms.fxMap = map;
      shader.uniforms.fxCutoff = cutoff;
      shader.uniforms.fxGain = gain;
      shader.vertexShader = injectVert(shader.vertexShader);
      shader.fragmentShader = injectFrag(shader.fragmentShader);
    };
  }

  /**
   * Three's default program cache key knows nothing about onBeforeCompile output. Without
   * this an unpatched material with identical parameters could share our program.
   *
   * ROUND 6 — `opts.tag` USED TO BE PART OF THIS KEY, AND THAT COST FOUR SHADER PROGRAMS.
   * fx takes six clones (soft / spray / hard / decal / text / screen) purely so it can set
   * six different sets of GL STATE — blending, depthWrite, depthTest, polygonOffset — and
   * three different uniform values. None of those affect the compiled shader: blending and
   * depth state are per-draw GL calls, and `fxCutoff` / `fxGain` are uniforms. The injected
   * source is byte-identical across all six. Tagging the key forced six identical programs
   * against a 45-program budget the build is already over (measured 50).
   *
   * This is SAFE because `customProgramCacheKey` is APPENDED to three's own parameter key
   * rather than replacing it: anything that genuinely changes the shader (material type,
   * map/alphaMap presence, fog, vertexColors, alphaTest, double-sidedness, output colour
   * space) is still in the key via three's own `getProgramCacheKey`. Two of our clones share
   * a program only when they would have compiled the same source anyway — and when they do,
   * three keeps each material's own `parameters.uniforms` object, so `fxGain` on the opaque
   * layer stays independent of the others. `material.type` is kept in the key explicitly so
   * a future `look` change that hands fx two different base material classes can never
   * collide two different injections onto one key.
   */
  const baseKey = `ppmfx2|${material.type}|${material.map ? 'm' : '_'}|${material.vertexColors ? 'c' : '_'}`;
  material.customProgramCacheKey = () => baseKey;

  // Lighting/normal work is dead code for us — the injection replaces gl_FragColor outright.
  if ('flatShading' in material) material.flatShading = false;
  if ('fog' in material) material.fog = true;

  material.needsUpdate = true;
  return material;
}

function injectVert(src) {
  let out = VERT_DECL + '\n' + src;
  for (const a of VERT_ANCHORS) {
    if (out.indexOf(a) >= 0) return out.replace(a, VERT_BODY + '\n' + a);
  }
  return appendInMain(out, VERT_BODY);
}

function injectFrag(src) {
  let out = FRAG_DECL + '\n' + src;
  for (const a of FRAG_ANCHORS) {
    if (out.indexOf(a) >= 0) return out.replace(a, FRAG_BODY + '\n' + a);
  }
  return appendInMain(out, FRAG_BODY);
}

/** Last resort: splice the body in before the final closing brace of main(). */
function appendInMain(src, body) {
  const i = src.lastIndexOf('}');
  if (i < 0) return src;
  return src.slice(0, i) + body + '\n' + src.slice(i);
}

/**
 * Build the instanced quad used by every fx layer. Separate geometry per layer because the
 * per-instance attributes live on the geometry.
 */
export function makeQuadGeometry(capacity) {
  const geo = new THREE.PlaneGeometry(1, 1);
  const color = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
  const alpha = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
  const rect = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
  color.setUsage(THREE.DynamicDrawUsage);
  alpha.setUsage(THREE.DynamicDrawUsage);
  rect.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aFxColor', color);
  geo.setAttribute('aFxAlpha', alpha);
  geo.setAttribute('aFxRect', rect);
  // Instances are scattered across the whole city; culling on the base quad's bounds is
  // meaningless, so the meshes set frustumCulled = false and this bound is never consulted.
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  return geo;
}

/** Convert an authored sRGB hex into the linear working space three renders in. */
const _c = new THREE.Color();
export function linearRgb(hex, out) {
  _c.setHex(hex, THREE.SRGBColorSpace);
  out[0] = _c.r; out[1] = _c.g; out[2] = _c.b;
  return out;
}
